import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";

import type { AdminFormId } from "@/shared/types/administrationTypes";
import type { PersonContact } from "@/shared/types/estateContact";

export const ADMIN_FORM_NAMES: Record<AdminFormId, string> = {
  "A-1": "Petition for Letters of Administration",
  "A-1-C": "Schedule C: Infants",
  "A-1-D": "Schedule D: Persons Under Disability",
  "A-2": "Administration Citation",
  "A-3": "Notice of Application for Letters",
  "A-4": "Affidavit of Mailing Notice of Application",
  "A-5": "Notice to Consul General",
  "A-8": "Waiver, Renunciation and Consent (Individual)",
  "A-10": "Affidavit of Service of Citation",
};

export const ADMIN_FORM_URLS: Record<AdminFormId, string> = {
  "A-1": "/administration/new_york/A-1_Petition_for_Letters_of_Administration.pdf",
  "A-1-C": "/administration/new_york/A-1_Schedule_C_Infants.pdf",
  "A-1-D": "/administration/new_york/A-1_Schedule_D_Persons_Under_Disability.pdf",
  "A-2": "/administration/new_york/A-2_Administration_Citation.pdf",
  "A-3": "/administration/new_york/A-3_Notice_of_Application_for_Letters.pdf",
  "A-4": "/administration/new_york/A-4_Affidavit_of_Mailing_Notice_of_Application.pdf",
  "A-5": "/administration/new_york/A-5_Notice_to_Consul_General.pdf",
  "A-8": "/administration/new_york/A-8_Waiver_Renunciation_Consent_Individual.pdf",
  "A-10": "/administration/new_york/A-10_Affidavit_of_Service_of_Citation_Adult.pdf",
};

export interface AdminFormRow {
  formId: AdminFormId;
  signerName: string | null;
  signerLabel: string;
  citedPersons?: string[];
  noticeRecipients?: string[];
}

export interface HelplineCard {
  type: "due_diligence" | "gal" | "bond" | "heirship";
  title: string;
  description: string;
}

interface AdminFormInput {
  contacts: PersonContact[];
  adminMode: string;
  proposedAdminName: string;
  proposedAdminTier: number | null;
  consents: Record<string, { name: string; status: string }>;
  coApplicantIds: number[];
  extendedFamilyHeirship: boolean;
}

function contactName(c: PersonContact): string {
  return [c.first_name, c.last_name].filter(Boolean).join(" ") || "Unknown";
}

export function getRequiredAdminForms(input: AdminFormInput): {
  forms: AdminFormRow[];
  helplineCards: HelplineCard[];
} {
  const {
    contacts,
    adminMode,
    proposedAdminName,
    proposedAdminTier,
    consents,
    coApplicantIds,
    extendedFamilyHeirship,
  } = input;

  const isSomeoneElse = adminMode === "someone_else";
  const adminLabel = isSomeoneElse ? `${proposedAdminName} signs` : "You sign";
  const distributees = contacts.filter((c) => c.is_distributee);

  const forms: AdminFormRow[] = [];
  const helplineCards: HelplineCard[] = [];
  const consentEntries = Object.entries(consents);

  // Collect co-applicants from Step 4 consents (co_apply choice)
  const step4CoApplicants = consentEntries
    .filter(([, e]) => e.status === "co_apply")
    .map(([, e]) => e.name);
  const hasCoApplicants = adminMode === "co" || step4CoApplicants.length > 0;

  // A-1 always included
  const a1Signer = hasCoApplicants
    ? `${isSomeoneElse ? proposedAdminName : "You"} + co-applicants sign`
    : adminLabel;
  forms.push({ formId: "A-1", signerName: null, signerLabel: a1Signer });

  // A-1 Schedule C: any minor distributee
  const hasMinor = distributees.some(
    (c) => c.additional_details?.includes("minor") && c.person_status !== "passed_away",
  );
  if (hasMinor) {
    forms.push({ formId: "A-1-C", signerName: null, signerLabel: "Attached to A-1" });
  }

  // A-1 Schedule D: any incapacitated distributee
  const hasIncapacitated = distributees.some(
    (c) => c.additional_details?.includes("incapacitated") && c.person_status !== "passed_away",
  );
  if (hasIncapacitated) {
    forms.push({ formId: "A-1-D", signerName: null, signerLabel: "Attached to A-1" });
  }

  // A-3 + A-4: any distributee with lower priority, or notice-only parents (spouse-only estate)
  const hasLowerPriority = proposedAdminTier !== null && distributees.some(
    (c) =>
      c.person_status !== "passed_away" &&
      (c.priority_tier ?? 99) > proposedAdminTier &&
      !coApplicantIds.includes(c.id),
  );
  const spouseOnly = distributees.length === 1 && distributees[0].relationship === "spouse";
  const hasNoticeParents = spouseOnly && contacts.some(
    (c) =>
      !c.is_distributee &&
      c.person_status !== "passed_away" &&
      c.relationship === "parent",
  );
  if (hasLowerPriority || hasNoticeParents) {
    const consentIds = new Set(
      consentEntries.filter(([, e]) => e.status === "consent" || e.status === "co_apply").map(([id]) => id),
    );
    const noticeRecipients: string[] = [];
    for (const c of distributees) {
      if (c.person_status === "passed_away") continue;
      if (proposedAdminTier !== null && (c.priority_tier ?? 99) <= proposedAdminTier) continue;
      if (proposedAdminTier === null) continue;
      if (coApplicantIds.includes(c.id)) continue;
      if (consentIds.has(String(c.id))) continue;
      const name = contactName(c);
      const rel = c.relationship ? getRelationshipLabel(c.relationship) : null;
      noticeRecipients.push(rel ? `${name}, ${rel.toLowerCase()}` : name);
    }
    if (hasNoticeParents) {
      for (const c of contacts) {
        if (c.is_distributee) continue;
        if (c.person_status === "passed_away") continue;
        if (c.relationship !== "parent") continue;
        const name = contactName(c);
        noticeRecipients.push(`${name}, parent`);
      }
    }
    forms.push({
      formId: "A-3",
      signerName: null,
      signerLabel: "You mail a copy to each person below",
      noticeRecipients,
    });
    forms.push({ formId: "A-4", signerName: null, signerLabel: adminLabel });
  }

  // A-8: one per consenting person
  for (const [, entry] of consentEntries) {
    if (entry.status === "consent") {
      forms.push({
        formId: "A-8",
        signerName: entry.name,
        signerLabel: `${entry.name} signs`,
      });
    }
  }

  // A-2: single citation listing all cited persons
  const citedPersons: string[] = [];
  for (const [, entry] of consentEntries) {
    if (entry.status === "uncooperative" || entry.status === "cannot_locate") {
      citedPersons.push(entry.name);
    }
  }
  // Auto-cited minors (same tier, can't serve)
  const autoMinors = distributees.filter(
    (c) =>
      c.additional_details?.includes("minor") &&
      c.person_status !== "passed_away" &&
      proposedAdminTier !== null &&
      c.priority_tier === proposedAdminTier,
  );
  for (const m of autoMinors) {
    citedPersons.push(contactName(m));
  }
  if (citedPersons.length > 0) {
    forms.push({
      formId: "A-2",
      signerName: null,
      signerLabel: "Issued by court",
      citedPersons,
    });
  }

  // A-10: if any "won't sign"
  const hasUncooperative = consentEntries.some(([, e]) => e.status === "uncooperative");
  if (hasUncooperative) {
    forms.push({ formId: "A-10", signerName: null, signerLabel: "Whoever serves" });
  }

  // Helpline cards
  const hasCannotLocate = consentEntries.some(([, e]) => e.status === "cannot_locate");
  if (hasCannotLocate) {
    helplineCards.push({
      type: "due_diligence",
      title: "Due Diligence Affidavit needed",
      description: "Contact our helpline for help drafting a Due Diligence Affidavit to support the citation.",
    });
  }

  if (hasMinor || hasIncapacitated) {
    helplineCards.push({
      type: "gal",
      title: "Guardian ad Litem will be appointed",
      description: "The court will appoint an independent attorney to review the case on behalf of minors or incapacitated persons.",
    });
  }

  const bondRequired = hasMinor || hasUncooperative || hasCannotLocate;
  if (bondRequired) {
    helplineCards.push({
      type: "bond",
      title: "Administrator's bond likely required",
      description: "Under NY law (SCPA 801/805), a bond is required because not all heirs can or will consent. Our team will help you source one.",
    });
  }

  if (extendedFamilyHeirship) {
    helplineCards.push({
      type: "heirship",
      title: "Affidavit of Heirship may be needed",
      description: "Extended family distributees may require an Affidavit of Heirship/Family Tree. Contact our helpline for county-specific requirements.",
    });
  }

  return { forms, helplineCards };
}
